/**
 * Carousel Block - Interactivity API View Script
 *
 * Initializes Embla Carousel from context config.
 *
 * @package BrandyBlocks
 */

import { store, getContext, getElement } from '@wordpress/interactivity';

const emblaInstances = new WeakMap();

function addDotBtnsAndClickHandlers( emblaApi, dotsNode ) {
	if ( ! dotsNode ) {
		return () => {};
	}
	let dotNodes = [];

	const addDotBtnsWithClickHandlers = () => {
		dotsNode.innerHTML = emblaApi
			.scrollSnapList()
			.map(
				() =>
					'<button class="embla__dot" type="button" aria-label="Carousel dot"></button>'
			)
			.join( '' );

		dotNodes = Array.from( dotsNode.querySelectorAll( '.embla__dot' ) );
		dotNodes.forEach( ( dotNode, index ) => {
			dotNode.addEventListener(
				'click',
				() => emblaApi.scrollTo( index ),
				false
			);
		} );
	};

	const toggleDotBtnsActive = () => {
		const previous = emblaApi.previousScrollSnap();
		const selected = emblaApi.selectedScrollSnap();
		if ( dotNodes[ previous ] ) {
			dotNodes[ previous ].classList.remove( 'embla__dot--selected' );
		}
		if ( dotNodes[ selected ] ) {
			dotNodes[ selected ].classList.add( 'embla__dot--selected' );
		}
	};

	emblaApi
		.on( 'init', addDotBtnsWithClickHandlers )
		.on( 'reInit', addDotBtnsWithClickHandlers )
		.on( 'init', toggleDotBtnsActive )
		.on( 'reInit', toggleDotBtnsActive )
		.on( 'select', toggleDotBtnsActive );

	return () => {
		dotsNode.innerHTML = '';
	};
}

function addPrevNextBtnsClickHandlers( emblaApi, prevBtn, nextBtn ) {
	if ( ! prevBtn || ! nextBtn ) {
		return () => {};
	}

	const scrollPrev = () => emblaApi.scrollPrev();
	const scrollNext = () => emblaApi.scrollNext();

	prevBtn.addEventListener( 'click', scrollPrev, false );
	nextBtn.addEventListener( 'click', scrollNext, false );

	const toggleState = () => {
		if ( emblaApi.canScrollPrev() ) {
			prevBtn.removeAttribute( 'disabled' );
		} else {
			prevBtn.setAttribute( 'disabled', 'disabled' );
		}
		if ( emblaApi.canScrollNext() ) {
			nextBtn.removeAttribute( 'disabled' );
		} else {
			nextBtn.setAttribute( 'disabled', 'disabled' );
		}
	};

	emblaApi
		.on( 'select', toggleState )
		.on( 'init', toggleState )
		.on( 'reInit', toggleState );

	return () => {
		prevBtn.removeAttribute( 'disabled' );
		nextBtn.removeAttribute( 'disabled' );
		prevBtn.removeEventListener( 'click', scrollPrev, false );
		nextBtn.removeEventListener( 'click', scrollNext, false );
	};
}

store( 'brandy/carousel', {
	callbacks: {
		init() {
			const ctx = getContext();
			const { ref } = getElement();

			if ( typeof window.EmblaCarousel === 'undefined' ) {
				return;
			}

			const emblaNode = ref.querySelector( '.embla' );
			if ( ! emblaNode ) {
				return;
			}

			const viewportNode = emblaNode.querySelector( '.embla__viewport' );
			if ( ! viewportNode ) {
				return;
			}

			const effect = ctx.effect ?? 'default';

			const options = {
				loop: ctx.loop ?? true,
				align: effect === 'center' ? 'center' : 'start',
				skipSnaps: true,
			};

			const plugins =
				ctx.autoPlay && window.EmblaCarouselAutoplay
					? [
							window.EmblaCarouselAutoplay( {
								playOnInit: true,
								delay: ctx.autoPlayDelay ?? 3000,
								stopOnInteraction: false,
								stopOnMouseEnter: ctx.pauseOnHover ?? true,
							} ),
					  ]
					: [];

			if ( effect === 'center' && window.EmblaCenterMode ) {
				const style = ctx.spotlightStyle ?? 'fade-scale';
				const scaleMap = {
					subtle: 0.95,
					medium: 0.9,
					bold: 0.85,
				};
				const scaleLevel = ctx.spotlightScale ?? 'medium';
				const hasScale =
					style === 'fade-scale' || style === 'scale';
				const hasFade =
					style === 'fade-scale' || style === 'fade';
				plugins.push(
					window.EmblaCenterMode( {
						fade: hasFade,
						scale: hasScale
							? scaleMap[ scaleLevel ] ?? 0.9
							: 1,
					} )
				);
			}

			const emblaApi = window.EmblaCarousel(
				viewportNode,
				options,
				plugins
			);

			const prevBtn = emblaNode.querySelector( '.embla__button--prev' );
			const nextBtn = emblaNode.querySelector( '.embla__button--next' );
			const dotsNode = emblaNode.querySelector( '.embla__dots' );

			const removePrevNext = addPrevNextBtnsClickHandlers(
				emblaApi,
				prevBtn,
				nextBtn
			);
			const removeDots = addDotBtnsAndClickHandlers( emblaApi, dotsNode );

			emblaApi.on( 'destroy', removePrevNext );
			emblaApi.on( 'destroy', removeDots );

			emblaInstances.set( ref, emblaApi );
		},

		destroy() {
			const { ref } = getElement();
			const emblaApi = emblaInstances.get( ref );
			if ( emblaApi ) {
				emblaApi.destroy();
				emblaInstances.delete( ref );
			}
		},
	},
} );
